import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, EmptyState, Loading, ModuleHero, Notice, StatusPill } from '../components/ui';
import { FEATURE, PROMPT_ROLE, RESOLVER, formatDateTime, formatPct, formatTokens, formatUsd, pctTone } from '../lib/format';
import type { PromptFamilyView, PromptOverview, PromptVersionStats } from '../types';

const TH = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#c084a0]';

export default function PromptLab() {
  const { data, loading, error } = useApi<PromptOverview>('/support/ai/prompts', 15000);
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);

  const family = useMemo(() => {
    if (!data?.families.length) return null;
    return data.families.find((f) => f.id === familyId) || data.families[0];
  }, [data, familyId]);

  const left = family?.versions.find((v) => v.id === leftId) || family?.active || family?.versions[0] || null;
  const right =
    family?.versions.find((v) => v.id === rightId) ||
    family?.versions.find((v) => v.id !== left?.id) ||
    null;

  if (loading && !data) return <Loading label="Loading prompt versions…" />;
  if (error && !data) return <Notice tone="rose">{error}</Notice>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <ModuleHero
        eyebrow="Prompt platform"
        title="How each prompt version responded"
        description="Every AI call is attributed to an immutable prompt version. Compare live vs canary vs archived on cost, fail rate, analyst acceptance, and the actual model output."
        actions={<span className="text-xs text-[#c084a0]">Updated {formatDateTime(data.generatedAt)} · auto-refresh 15s</span>}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat label="Prompt families" value={data.totals.families} />
        <MiniStat label="Versions" value={data.totals.versions} />
        <MiniStat label="Canaries running" value={data.totals.canaryFamilies} />
        <MiniStat label="Drafts waiting" value={data.totals.pendingDrafts} />
      </div>

      {data.families.length === 0 ? (
        <EmptyState title="No prompt families yet" body="Save a rating prompt in Admin to create the first versioned family." />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
          <Card title="Families">
            <ul className="-mx-2 divide-y divide-[#fdf2f8]">
              {data.families.map((f) => {
                const active = f.id === family?.id;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setFamilyId(f.id);
                        setLeftId(f.activeVersionId);
                        setRightId(f.canaryVersionId || (f.draftVersionId !== f.activeVersionId ? f.draftVersionId : null));
                      }}
                      className={`flex w-full flex-col items-start gap-1 px-4 py-3 text-left ${active ? 'bg-[#fdf2f8]' : 'hover:bg-[#fff7fb]'}`}
                    >
                      <span className="text-sm font-bold text-[#6b0030]">{f.label}</span>
                      <span className="text-[11px] text-[#c084a0]">
                        {PROMPT_ROLE[f.role] || f.role}
                        {f.countryCode ? ` · ${f.countryCode}` : ' · global'}
                      </span>
                      <span className="flex flex-wrap gap-1">
                        {f.active && <StatusPill label={`Live v${f.active.version}`} tone="green" />}
                        {f.canary && <StatusPill label={`Canary v${f.canary.version} ${f.canaryPercent}%`} tone="amber" />}
                        {f.draft && f.draft.status === 'DRAFT' && <StatusPill label={`Draft v${f.draft.version}`} tone="pink" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {family && (
            <div className="space-y-4">
              <CompareStrip family={family} left={left} right={right} onLeft={setLeftId} onRight={setRightId} />
              <VersionTable family={family} selected={[left?.id, right?.id]} onSelect={(id) => (leftId ? setRightId(id) : setLeftId(id))} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border-y border-[#fce7f3] py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className="mt-1 text-2xl font-black tabular-nums text-[#6b0030]">{value}</p>
    </div>
  );
}

function CompareStrip({
  family,
  left,
  right,
  onLeft,
  onRight,
}: {
  family: PromptFamilyView;
  left: PromptVersionStats | null;
  right: PromptVersionStats | null;
  onLeft: (id: string) => void;
  onRight: (id: string) => void;
}) {
  return (
    <Card title={`${family.label}`} subtitle="Pick two versions. Acceptance is analyst-confirmed ratings (±5 score, same grade).">
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-[#9d0050]">
          Version A
          <select className="mt-1 w-full rounded-lg border border-[#f9c0d8] bg-[#fff5f8] px-2 py-1.5 text-sm text-[#6b0030]" value={left?.id || ''} onChange={(e) => onLeft(e.target.value)}>
            {family.versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version} · {v.status}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-[#9d0050]">
          Version B
          <select className="mt-1 w-full rounded-lg border border-[#f9c0d8] bg-[#fff5f8] px-2 py-1.5 text-sm text-[#6b0030]" value={right?.id || ''} onChange={(e) => onRight(e.target.value)}>
            <option value="">—</option>
            {family.versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version} · {v.status}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <VersionPane version={left} />
        <VersionPane version={right} emptyLabel="Select a second version to compare responses." />
      </div>
    </Card>
  );
}

function VersionPane({ version, emptyLabel }: { version: PromptVersionStats | null; emptyLabel?: string }) {
  if (!version) {
    return <p className="rounded-xl border border-dashed border-[#fce7f3] px-4 py-10 text-center text-sm text-[#c084a0]">{emptyLabel}</p>;
  }
  return (
    <div className="rounded-xl border border-[#fce7f3] bg-[#fffafc] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-black text-[#6b0030]">v{version.version}</span>
        <StatusPill label={version.status} tone={version.status === 'ACTIVE' ? 'green' : version.status === 'CANARY' ? 'amber' : version.status === 'DRAFT' ? 'pink' : 'slate'} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div>
          <dt className="text-[#c084a0]">Calls</dt>
          <dd className="font-bold text-[#6b0030]">{version.calls}</dd>
        </div>
        <div>
          <dt className="text-[#c084a0]">Fail rate</dt>
          <dd className={`font-bold ${pctTone(version.failPct == null ? null : 100 - version.failPct) === 'rose' ? 'text-[#be123c]' : 'text-[#6b0030]'}`}>{formatPct(version.failPct)}</dd>
        </div>
        <div>
          <dt className="text-[#c084a0]">Cost</dt>
          <dd className="font-bold text-[#6b0030]">{formatUsd(version.costUsd)}</dd>
        </div>
        <div>
          <dt className="text-[#c084a0]">Avg latency</dt>
          <dd className="font-bold text-[#6b0030]">{version.calls ? `${(version.avgLatencyMs / 1000).toFixed(1)}s` : '—'}</dd>
        </div>
        <div>
          <dt className="text-[#c084a0]">Ratings accepted</dt>
          <dd className="font-bold text-[#6b0030]">{formatPct(version.ratings.acceptedPct)}</dd>
        </div>
        <div>
          <dt className="text-[#c084a0]">Avg AI score</dt>
          <dd className="font-bold text-[#6b0030]">{version.ratings.avgAiScore ?? '—'}</dd>
        </div>
      </dl>
      <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Recent responses</p>
      {version.traces.length === 0 ? (
        <p className="mt-1 text-xs text-[#c084a0]">No calls on this version yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {version.traces.map((t) => (
            <li key={t.id} className="rounded-lg bg-white p-2.5 text-xs text-[#6b0030]">
              <div className="flex flex-wrap items-center gap-2">
                {t.company ? (
                  <Link to={`/companies/${t.company.id}`} className="font-bold text-[#d6006e] hover:underline">
                    {t.company.name}
                  </Link>
                ) : (
                  <span className="font-bold">Sample test</span>
                )}
                <span className="text-[#c084a0]">{FEATURE[t.feature] || t.feature}</span>
                {t.resolverReason && <StatusPill label={RESOLVER[t.resolverReason] || t.resolverReason} tone={t.resolverReason === 'canary' ? 'amber' : 'slate'} />}
                {!t.success && <StatusPill label="Failed" tone="rose" />}
              </div>
              {t.outputPreview && <pre className="mt-1.5 max-h-28 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-[#7a1f4a]">{t.outputPreview}</pre>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VersionTable({ family, selected, onSelect }: { family: PromptFamilyView; selected: Array<string | undefined>; onSelect: (id: string) => void }) {
  return (
    <Card title="All versions" subtitle={`${family.versions.length} immutable snapshots`} className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-xs">
          <thead className="border-b border-[#fce7f3]">
            <tr>
              <th className={TH}>Ver</th>
              <th className={TH}>Status</th>
              <th className={`${TH} text-right`}>Calls</th>
              <th className={`${TH} text-right`}>Fail</th>
              <th className={`${TH} text-right`}>Accepted</th>
              <th className={`${TH} text-right`}>Cost</th>
              <th className={TH}>Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#fdf2f8] text-[#6b0030]">
            {family.versions.map((v) => (
              <tr key={v.id} className={selected.includes(v.id) ? 'bg-[#fdf2f8]' : 'cursor-pointer hover:bg-[#fff7fb]'} onClick={() => onSelect(v.id)}>
                <td className="px-3 py-2 font-black">v{v.version}</td>
                <td className="px-3 py-2">
                  <StatusPill label={v.status} tone={v.status === 'ACTIVE' ? 'green' : v.status === 'CANARY' ? 'amber' : v.status === 'DRAFT' ? 'pink' : 'slate'} />
                </td>
                <td className="px-3 py-2 text-right">{v.calls}</td>
                <td className="px-3 py-2 text-right">{formatPct(v.failPct)}</td>
                <td className="px-3 py-2 text-right">{formatPct(v.ratings.acceptedPct)}</td>
                <td className="px-3 py-2 text-right font-semibold">{formatUsd(v.costUsd)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#c084a0]">{formatDateTime(v.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 text-[11px] text-[#b0708f]">{formatTokens(family.versions.reduce((n, v) => n + v.tokens, 0))} tokens attributed to this family.</p>
    </Card>
  );
}
